import React, { useContext, useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { AppContext } from '../../context/AppContext';
import { Carrera, ResultadoCarrera, Socio } from '../../models/types';
import { CarreraServiceFirestore } from '../../services/CarreraServiceFirestore';
import { sancionServiceFirestore } from '../../services/SancionServiceFirestore';
import { FaSave, FaArrowLeft, FaPlus, FaTrash, FaMedal, FaFlag, FaStopwatch, FaHourglass, FaArrowUp, FaArrowDown, FaUsers, FaTrophy, FaCalculator, FaFileImport } from '../../components/Icons';
import { ImportResultadosCarrera } from '../../components/ImportResultadosCarrera';
import { theme } from '../../theme';
import { CIRCUITOS } from '../../data/circuitos';

const Container = styled.div`
  padding: 24px;
  max-width: 1280px;
  margin: 0 auto;
  background-color: ${theme.colors.background.default};
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 32px;
`;

const Title = styled.h1`
  color: ${theme.colors.primary.main};
  font-weight: ${theme.typography.fontWeights.bold};
  font-size: 2.2rem;
  position: relative;
  padding-bottom: 12px;
  display: flex;
  align-items: center;
  
  &:after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    width: 60px;
    height: 4px;
    background-color: ${theme.colors.secondary.main};
    border-radius: 2px;
  }
  
  svg {
    margin-right: 16px;
    color: ${theme.colors.secondary.main};
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 12px;
`;

const Button = styled.button`
  background-color: ${theme.colors.secondary.main};
  color: ${theme.colors.secondary.contrastText};
  border: none;
  border-radius: ${theme.borderRadius.small};
  padding: 12px 20px;
  cursor: pointer;
  display: flex;
  align-items: center;
  font-weight: ${theme.typography.fontWeights.medium};
  transition: all 0.3s ease;
  box-shadow: ${theme.shadows.small};
  
  &:hover {
    background-color: ${theme.colors.secondary.dark};
    transform: translateY(-2px);
    box-shadow: ${theme.shadows.medium};
  }
  
  svg {
    margin-right: 10px;
    font-size: 1.1rem;
  }
`;

const BackButton = styled(Button)`
  background-color: ${theme.colors.background.dark};
  
  &:hover {
    background-color: ${theme.colors.primary.light};
  }
`;

const Form = styled.form`
  background-color: ${theme.colors.background.paper};
  border-radius: ${theme.borderRadius.medium};
  padding: 32px;
  box-shadow: ${theme.shadows.medium};
  margin-bottom: 32px;
  border: 1px solid rgba(0, 0, 0, 0.05);
`;

const FormGroup = styled.div`
  margin-bottom: 24px;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 8px;
  font-weight: ${theme.typography.fontWeights.semibold};
  color: ${theme.colors.text.primary};
  font-size: 1rem;
`;

const Input = styled.input`
  width: 100%;
  padding: 12px 16px;
  border: 1px solid rgba(0, 0, 0, 0.1);
  border-radius: ${theme.borderRadius.small};
  font-size: 1rem;
  transition: all 0.2s ease;
  background-color: ${theme.colors.background.default};
  color: ${theme.colors.text.primary};
  
  &:focus {
    outline: none;
    border-color: ${theme.colors.primary.main};
    box-shadow: 0 0 0 2px rgba(16, 19, 37, 0.1);
  }
  
  &::placeholder {
    color: ${theme.colors.text.hint};
  }
`;

const Select = styled.select`
  width: 100%;
  padding: 12px 16px;
  border: 1px solid rgba(0, 0, 0, 0.1);
  border-radius: ${theme.borderRadius.small};
  font-size: 1rem;
  transition: all 0.2s ease;
  background-color: ${theme.colors.background.default};
  color: ${theme.colors.text.primary};
  appearance: none;
  background-image: url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="6 9 12 15 18 9"></polyline></svg>');
  background-repeat: no-repeat;
  background-position: right 12px center;
  background-size: 16px;
  
  &:focus {
    outline: none;
    border-color: ${theme.colors.primary.main};
    box-shadow: 0 0 0 2px rgba(16, 19, 37, 0.1);
  }
`;

const Card = styled.div`
  background-color: ${theme.colors.background.paper};
  border-radius: ${theme.borderRadius.medium};
  padding: 24px;
  box-shadow: ${theme.shadows.medium};
  margin-bottom: 24px;
  border: 1px solid rgba(0, 0, 0, 0.05);
  transition: all 0.3s ease;
  
  &:hover {
    box-shadow: ${theme.shadows.large};
    transform: translateY(-2px);
  }
`;

const CardTitle = styled.h2`
  color: ${theme.colors.primary.main};
  margin-bottom: 24px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-weight: ${theme.typography.fontWeights.semibold};
  position: relative;
  padding-bottom: 8px;
  
  &:after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    width: 40px;
    height: 3px;
    background-color: ${theme.colors.secondary.main};
    border-radius: 2px;
  }
  
  svg {
    margin-right: 12px;
    color: ${theme.colors.secondary.main};
  }
`;

const SectionTitle = styled.h2`
  color: ${theme.colors.primary.main};
  margin-top: 40px;
  margin-bottom: 24px;
  font-size: 1.5rem;
  font-weight: ${theme.typography.fontWeights.semibold};
  position: relative;
  padding-bottom: 8px;
  display: flex;
  align-items: center;
  
  &:after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    width: 40px;
    height: 3px;
    background-color: ${theme.colors.secondary.main};
    border-radius: 2px;
  }
  
  svg {
    margin-right: 12px;
    color: ${theme.colors.secondary.main};
  }
`;

const TableContainer = styled.div`
  overflow: hidden;
  border-radius: ${theme.borderRadius.medium};
  box-shadow: ${theme.shadows.medium};
  background-color: ${theme.colors.background.paper};
  margin-bottom: 24px;
  border: 1px solid rgba(0, 0, 0, 0.05);
`;

const Table = styled.table`
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  
  th, td {
    padding: 16px;
    text-align: left;
  }
  
  th {
    background-color: ${theme.colors.primary.main};
    color: ${theme.colors.primary.contrastText};
    font-weight: ${theme.typography.fontWeights.semibold};
    position: sticky;
    top: 0;
    z-index: 10;
  }
  
  th:first-child {
    border-top-left-radius: ${theme.borderRadius.small};
  }
  
  th:last-child {
    border-top-right-radius: ${theme.borderRadius.small};
  }
  
  tr:last-child td:first-child {
    border-bottom-left-radius: ${theme.borderRadius.small};
  }
  
  tr:last-child td:last-child {
    border-bottom-right-radius: ${theme.borderRadius.small};
  }
  
  tr:nth-child(even) td {
    background-color: rgba(0, 0, 0, 0.02);
  }
  
  td {
    border-bottom: 1px solid rgba(0, 0, 0, 0.05);
    color: ${theme.colors.text.primary};
  }
  
  tr:hover td {
    background-color: rgba(228, 49, 46, 0.05);
  }
`;

const Th = styled.th`
  background-color: ${theme.colors.primary.main};
  color: ${theme.colors.primary.contrastText};
  font-weight: ${theme.typography.fontWeights.semibold};
  padding: 16px;
  text-align: left;
`;

const Td = styled.td`
  padding: 12px 15px;
  border-bottom: 1px solid #ddd;
`;

const ActionButton = styled.button`
  background-color: transparent;
  border: none;
  cursor: pointer;
  margin-right: 12px;
  color: ${theme.colors.primary.main};
  font-size: 1.1rem;
  padding: 8px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  
  &:hover {
    color: ${theme.colors.secondary.main};
    background-color: rgba(0, 0, 0, 0.05);
    transform: translateY(-2px);
  }
`;

const DragHandle = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: grab;
  color: #6c757d;
  
  &:hover {
    color: #0f3460;
  }
`;

const ResultadoRow = styled.tr<{ $isDragging?: boolean }>`
  background-color: ${props => props.$isDragging ? '#f0f8ff' : 'white'};
  cursor: grab;
  transition: background-color 0.2s;
  
  &:hover {
    background-color: #f5f5f5;
  }
`;

const InfoMessage = styled.div`
  background-color: #e2f0ff;
  border-left: 4px solid #0f3460;
  padding: 15px;
  margin-bottom: 20px;
  border-radius: 4px;
`;

const CarreraDetail: React.FC = (): React.ReactElement => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { data, loadData } = useContext(AppContext);
  const [carrera, setCarrera] = useState<Carrera | null>(null);
  const [hasChanges, setHasChanges] = useState(false);
  const [formData, setFormData] = useState({
    nombre: '',
    ligaId: '',
    circuito: '',
    fecha: '',
    notas: '',
    publicada: false,
    sistemaPuntuacionId: ''
  });
  
  // Estado para los resultados de la carrera
  const [resultados, setResultados] = useState<ResultadoCarrera[]>([]);
  const [draggingIndex, setDraggingIndex] = useState<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);
  
  // Estado para los extras
  const [extras, setExtras] = useState<{
    vueltaRapida: string;
    polePosition: string;
    mejorConsistencia: string;
    segundaMejorConsistencia: string;
  }>({
    vueltaRapida: '',
    polePosition: '',
    mejorConsistencia: '',
    segundaMejorConsistencia: ''
  });
  
  // Estado para los pilotos DNF y no presentados
  const [pilotosDNF, setPilotosDNF] = useState<string[]>([]);
  const [pilotosNoPresentados, setPilotosNoPresentados] = useState<string[]>([]);
  
  // Estado para el diálogo de importación
  const [showImportDialog, setShowImportDialog] = useState(false);
  
  // La función calcularPuntosPiloto ya está definida más abajo en el archivo

  // Función para calcular los puntos totales de un resultado
  const calcularPuntosTotales = (resultado: ResultadoCarrera): number => {
    try {
      // Obtener los puntos base de la posición
      let puntos = resultado.puntos || 0;
      
      // Sumar puntos por vuelta rápida si corresponde
      if (resultado.vueltaRapida) {
        puntos += 1; // Ajustar según el sistema de puntuación
      }
      
      // Sumar puntos por pole position si corresponde
      if (resultado.pole) {
        puntos += 1; // Ajustar según el sistema de puntuación
      }
      
      // Restar puntos por incidentes (si aplica)
      if (resultado.incidentes && resultado.incidentes > 0) {
        puntos = Math.max(0, puntos - (resultado.incidentes * 1)); // 1 punto por incidente
      }
      
      // Asegurar que no haya puntos negativos
      return Math.max(0, puntos);
    } catch (error) {
      console.error('Error al calcular puntos totales:', error);
      return 0;
    }
  };

  // Manejador para importar resultados
  const handleImportResultados = (resultados: any[]) => {
    console.log('DEBUG - handleImportResultados - Iniciando importación de resultados');
    console.log('DEBUG - handleImportResultados - Datos recibidos:', resultados);
    
    if (!resultados || !Array.isArray(resultados)) {
      console.error('DEBUG - handleImportResultados - Error: No se recibieron resultados válidos');
      return;
    }
    
    try {
      // Actualizar los resultados
      const nuevosResultados: ResultadoCarrera[] = resultados.map((r, index) => ({
        id: `temp-${Date.now()}-${index}`,
        socioId: r.socioId,
        carreraId: id || '',
        posicion: r.posicion || index + 1,
        puntos: calcularPuntosPiloto(index + 1, r.socioId),
        dnf: r.dnf || false,
        vueltaRapida: r.vueltaRapida || false,
        pole: r.pole || false,
        noParticipacion: r.dns || false,
        incidentes: r.incidentes || 0,
        salidasPista: r.salidasPista || 0,
        sancionesAplicadas: r.sancionesAplicadas || [],
        puntosTotales: 0
      }));

      // Calcular puntos totales
      nuevosResultados.forEach(resultado => {
        resultado.puntosTotales = calcularPuntosTotales(resultado);
      });

      // Actualizar DNFs
      const dnfIds = nuevosResultados
        .filter(r => r.dnf)
        .map(r => r.socioId);
      setPilotosDNF(dnfIds);

      // Actualizar DNS
      const dnsIds = nuevosResultados
        .filter(r => r.noParticipacion)
        .map(r => r.socioId);
      setPilotosNoPresentados(dnsIds);

      // Actualizar resultados
      setResultados(nuevosResultados);
      setHasChanges(true);
      setShowImportDialog(false);
      
      console.log('DEBUG - handleImportResultados - Resultados importados correctamente:', nuevosResultados);
    } catch (error) {
      console.error('DEBUG - handleImportResultados - Error al importar resultados:', error);
      // Mostrar mensaje de error al usuario
      alert('Error al importar los resultados. Por favor, verifica el formato de los datos.');
    }
  };

  // Manejador para cambiar el estado DNF de un piloto
  const handleDNFChange = (socioId: string) => {
    setPilotosDNF(prev => {
      if (prev.includes(socioId)) {
        return prev.filter(id => id !== socioId);
      } else {
        return [...prev, socioId];
      }
    });
  };
  
  // Manejador para cambiar el estado No Presentado de un piloto
  const handleNoPresentadoChange = (socioId: string) => {
    setPilotosNoPresentados(prev => {
      if (prev.includes(socioId)) {
        return prev.filter(id => id !== socioId);
      } else {
        return [...prev, socioId];
      }
    });
  };
  
  // Cargar datos de la carrera
  useEffect(() => {
    if (id === 'new') {
      // Nueva carrera
      setCarrera(null);
      // Si hay liga seleccionada, rellenar resultados con los socios de esa liga
      const params = new URLSearchParams(location.search);
      const ligaId = params.get('liga') || (data.ligas.length > 0 ? data.ligas[0].id : '');
      // Obtener el sistema de puntuación de la liga seleccionada
      const ligaSeleccionada = data.ligas.find(l => l.id === ligaId);
      const sistemaPuntuacionId = ligaSeleccionada ? ligaSeleccionada.sistemaPuntuacion : 
        (data.sistemasPuntuacion.length > 0 ? data.sistemasPuntuacion[0].id : '');
      
      setFormData({
        nombre: '',
        ligaId: ligaId,
        circuito: '',
        fecha: new Date().toISOString().split('T')[0],
        notas: '',
        publicada: false, // Por defecto, las nuevas carreras no están publicadas
        sistemaPuntuacionId: sistemaPuntuacionId
      });
      // Rellenar resultados automáticamente usando la función centralizada
      setTimeout(() => {
        console.log(`DEBUG - useEffect - Inicializando resultados para la liga: ${ligaId}`);
        actualizarResultadosPorLiga(ligaId);
      }, 100);
      
      // Crear un array vacío para evitar errores mientras se cargan los datos
      const resultadosIniciales: ResultadoCarrera[] = [];
      setResultados(resultadosIniciales);
    } else {
      // Carrera existente
      const carreraExistente = data.carreras.find(c => c.id === id);
      if (carreraExistente) {
        setCarrera(carreraExistente);
        // Obtener el sistema de puntuación de la carrera o el de la liga si no tiene uno específico
        const ligaSeleccionada = data.ligas.find(l => l.id === carreraExistente.ligaId);
        const sistemaPuntuacionId = carreraExistente.sistemaPuntuacionId || 
          (ligaSeleccionada ? ligaSeleccionada.sistemaPuntuacion : 
          (data.sistemasPuntuacion.length > 0 ? data.sistemasPuntuacion[0].id : ''));
        
        setFormData({
          nombre: carreraExistente.nombre,
          ligaId: carreraExistente.ligaId,
          circuito: carreraExistente.circuito,
          fecha: carreraExistente.fecha,
          notas: carreraExistente.notas || '',
          publicada: carreraExistente.publicada || false, // Usar el valor existente o false por defecto
          sistemaPuntuacionId: sistemaPuntuacionId
        });
        
        // Verificar si hay resultados existentes
        if (carreraExistente.resultados && carreraExistente.resultados.length > 0) {
          console.log('DEBUG - useEffect - Cargando resultados existentes:', carreraExistente.resultados.length);
          // Cargar resultados existentes
          setResultados(carreraExistente.resultados);
          
          // Cargar extras
          const vueltaRapida = carreraExistente.resultados.find(r => r.vueltaRapida)?.socioId || '';
          const polePosition = carreraExistente.resultados.find(r => r.pole || r.polePosition)?.socioId || '';
          const mejorConsistencia = carreraExistente.resultados.find(r => r.mejorConsistencia)?.socioId || '';
          const segundaMejorConsistencia = carreraExistente.resultados.find(r => r.segundaMejorConsistencia)?.socioId || '';
          
          setExtras({
            vueltaRapida,
            polePosition,
            mejorConsistencia,
            segundaMejorConsistencia
          });
          
          // Cargar pilotos DNF
          setPilotosDNF(carreraExistente.resultados.filter(r => r.dnf).map(r => r.socioId));
          
          // Cargar pilotos no presentados
          setPilotosNoPresentados(carreraExistente.resultados.filter(r => r.noParticipacion).map(r => r.socioId));
        } else {
          console.log('DEBUG - useEffect - No hay resultados existentes, cargando pilotos automáticamente');
          // Establecer resultados vacíos para evitar problemas
          setResultados([]);
          
          // Cargar automáticamente los pilotos después de que el componente se monte completamente
          setTimeout(() => {
            // Asegurarse de que tenemos la ligaId correcta
            if (carreraExistente.ligaId) {
              // Obtener la liga seleccionada
              const ligaSeleccionada = data.ligas.find(liga => liga.id === carreraExistente.ligaId);
              if (ligaSeleccionada) {
                console.log(`DEBUG - useEffect - Liga seleccionada: ${ligaSeleccionada.nombre}, Categoría: ${ligaSeleccionada.categoria}`);
                
                // Filtrar socios por Categoria
                const categoriaFiltro = ligaSeleccionada.categoria || '';
                if (categoriaFiltro) {
                  const sociosFiltrados = data.socios.filter(socio => {
                    const socioCategoria = (socio.Categoria || '').trim().toUpperCase();
                    const categoriaFiltroUpper = categoriaFiltro.trim().toUpperCase();
                    return socioCategoria === categoriaFiltroUpper;
                  });
                  
                  console.log(`DEBUG - useEffect - Encontrados ${sociosFiltrados.length} socios con categoría '${categoriaFiltro}'`);
                  
                  if (sociosFiltrados.length > 0) {
                    const resultadosIniciales: ResultadoCarrera[] = sociosFiltrados.map((socio, idx) => ({
                      id: `resultado_${socio.id}_${Date.now()}_${idx}`,
                      socioId: socio.id,
                      carreraId: '',
                      posicion: idx + 1,
                      puntos: 0,
                      vueltaRapida: false,
                      pole: false,
                      polePosition: false,
                      mejorConsistencia: false,
                      segundaMejorConsistencia: false,
                      dnf: false,
                      noParticipacion: false,
                      incidentes: 0,
                      salidasPista: 0,
                      puntosAdicionales: 0,
                      sancionesAplicadas: [],
                      puntosTotales: 0
                    }));
                    
                    setResultados(resultadosIniciales);
                    setExtras({
                      vueltaRapida: '',
                      polePosition: '',
                      mejorConsistencia: '',
                      segundaMejorConsistencia: ''
                    });
                    setPilotosDNF([]);
                    console.log(`DEBUG - useEffect - Cargados ${resultadosIniciales.length} pilotos automáticamente`);
                  }
                }
              }
            }
          }, 1000); // Aumentar el retraso para asegurar que todos los datos están disponibles
        }
      }
    }
  }, [id, data, location.search]);
  
  // Obtener socios de la liga actual
  const getSociosLiga = () => {
    console.log('DEBUG - getSociosLiga - formData:', formData);
    console.log('DEBUG - getSociosLiga - data.socios:', data.socios);
    console.log('DEBUG - getSociosLiga - data.ligas:', data.ligas);
    
    if (!formData.ligaId) {
      console.log('DEBUG - getSociosLiga - No hay ligaId seleccionada');
      return [];
    }
    
    // Obtener la liga seleccionada
    const ligaSeleccionada = data.ligas.find(liga => liga.id === formData.ligaId);
    if (!ligaSeleccionada) {
      console.log('DEBUG - getSociosLiga - Liga no encontrada');
      return [];
    }
    
    console.log('DEBUG - getSociosLiga - Liga seleccionada:', ligaSeleccionada);
    
    // Usar directamente el campo categoria de la liga
    const categoriaFiltro = ligaSeleccionada.categoria || '';
    console.log(`DEBUG - getSociosLiga - Usando categoría de la liga: '${categoriaFiltro}'`);
    
    if (!categoriaFiltro) {
      console.log('DEBUG - getSociosLiga - La liga no tiene categoría definida');
      return [];
    }
    
    // Filtrar socios por Categoria
    const sociosFiltrados = data.socios.filter(socio => {
      const socioCategoria = (socio.Categoria || '').trim().toUpperCase();
      const categoriaFiltroUpper = categoriaFiltro.trim().toUpperCase();
      console.log(`DEBUG - Comparando socio ${socio.display_name || socio.nombre}: Categoria=${socioCategoria}, Liga Categoria=${categoriaFiltroUpper}, Match=${socioCategoria === categoriaFiltroUpper}`);
      return socioCategoria === categoriaFiltroUpper;
    });
    
    console.log(`DEBUG - getSociosLiga - Encontrados ${sociosFiltrados.length} socios con categoría '${categoriaFiltro}'`);
    return sociosFiltrados;
  };
  
  // Vista previa de participantes de la liga seleccionada
  const getSociosPreview = () => {
    return getSociosLiga();
  };



  // Manejar cambios en el formulario principal
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };
  
  // Función para calcular puntos según el sistema seleccionado
  const calcularPuntosPiloto = (posicion: number, socioId: string) => {
    // Obtener el sistema de puntuación seleccionado
    const sistemaPuntuacionId = formData.sistemaPuntuacionId || '';
    const sistemaPuntuacion = data.sistemasPuntuacion.find(s => s.id === sistemaPuntuacionId);
    
    // Calcular puntos según la posición
    let puntos = 0;
    if (sistemaPuntuacion && posicion <= sistemaPuntuacion.puntosPorPosicion.length) {
      // Usar el sistema de puntuación seleccionado
      puntos = sistemaPuntuacion.puntosPorPosicion[posicion - 1];
    } else if (posicion <= 50) {
      // Sistema por defecto ampliado a 50 posiciones
      if (posicion <= 24) {
        puntos = 25 - posicion; // Sistema clásico para posiciones 1-24
      } else {
        puntos = 0; // 0 puntos para posiciones 25-50
      }
    }
    
    // Puntos adicionales por extras
    let puntosAdicionales = 0;
    if (sistemaPuntuacion) {
      if (socioId === extras.vueltaRapida) {
        puntosAdicionales += sistemaPuntuacion.puntosAdicionales.vueltaRapida;
      }
      if (socioId === extras.polePosition) {
        puntosAdicionales += sistemaPuntuacion.puntosAdicionales.polePosition;
      }
      if (socioId === extras.mejorConsistencia) {
        puntosAdicionales += sistemaPuntuacion.puntosAdicionales.mejorConsistencia;
      }
      if (socioId === extras.segundaMejorConsistencia) {
        puntosAdicionales += sistemaPuntuacion.puntosAdicionales.segundaMejorConsistencia;
      }
    } else {
      // Sistema por defecto
      if (socioId === extras.vueltaRapida) puntosAdicionales += 1;
      if (socioId === extras.polePosition) puntosAdicionales += 0;
      if (socioId === extras.mejorConsistencia) puntosAdicionales += 2;
      if (socioId === extras.segundaMejorConsistencia) puntosAdicionales += 1;
    }
    
    return puntos + puntosAdicionales;
  };
  
  // Manejar cambios en la liga seleccionada
  const handleLigaChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const { name, value } = e.target;
    console.log('DEBUG - handleLigaChange - Liga seleccionada:', value);
    
    // Confirmar si ya hay resultados
    if (resultados.length > 0 && !window.confirm('Cambiar la liga sobrescribirá la lista de participantes. ¿Deseas continuar?')) {
      return;
    }
    
    // Obtener el sistema de puntuación de la liga seleccionada
    const ligaSeleccionada = data.ligas.find(l => l.id === value);
    const sistemaPuntuacionId = ligaSeleccionada ? ligaSeleccionada.sistemaPuntuacion : 
      (data.sistemasPuntuacion.length > 0 ? data.sistemasPuntuacion[0].id : '');
    
    // Actualizar el formulario con la nueva liga y su sistema de puntuación
    setFormData(prev => ({ 
      ...prev, 
      ligaId: value,
      sistemaPuntuacionId: sistemaPuntuacionId
    }));
    
    // Actualizar los resultados con los pilotos de la nueva liga
    actualizarResultadosPorLiga(value);
    
    // Marcar que hay cambios pendientes
    setHasChanges(true);
  };

  // Actualizar puntos cuando cambia el sistema de puntuación
  const handleSistemaPuntuacionChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const { name, value } = e.target;
    
    // Actualizar el formulario con el nuevo sistema
    setFormData(prev => ({ ...prev, [name]: value }));
    
    // Preguntar si desea recalcular los puntos
    if (resultados.length > 0 && window.confirm('¿Deseas recalcular los puntos según el nuevo sistema de puntuación?')) {
      // Recalcular puntos para todos los pilotos
      const updatedResultados = resultados.map(resultado => ({
        ...resultado,
        puntos: calcularPuntosPiloto(resultado.posicion, resultado.socioId)
      }));
      
      setResultados(updatedResultados);
      setHasChanges(true);
    }
  };
  
  // Botón para actualizar participantes manualmente (edición)
  const actualizarParticipantes = () => {
    if (!formData.ligaId) return;
    if (!window.confirm('Esto sobrescribirá la lista de participantes con los socios actuales de la liga. ¿Deseas continuar?')) return;
    
    console.log('DEBUG - actualizarParticipantes - Actualizando participantes para la liga:', formData.ligaId);
    
    // Usar la misma lógica mejorada de getSociosLiga para obtener los socios de la liga
    const sociosLiga = getSociosLiga();
    console.log(`DEBUG - actualizarParticipantes - Encontrados ${sociosLiga.length} socios para la liga`);
    
    if (sociosLiga.length === 0) {
      alert('No se encontraron pilotos para esta liga. Asegúrate de que la categoría de la liga coincide con la categoría de los pilotos.');
      return;
    }
    
    const resultadosIniciales: ResultadoCarrera[] = sociosLiga.map((socio, idx) => {
      console.log(`DEBUG - actualizarParticipantes - Añadiendo piloto: ${socio.display_name || socio.nombre}`);
      return {
        id: `resultado_${socio.id}_${Date.now()}_${idx}`,
        socioId: socio.id,
        carreraId: '',
        posicion: idx + 1,
        puntos: 0,
        vueltaRapida: false,
        pole: false,
        polePosition: false,
        mejorConsistencia: false,
        segundaMejorConsistencia: false,
        dnf: false,
        noParticipacion: false,
        incidentes: 0,
        salidasPista: 0,
        puntosAdicionales: 0,
        sancionesAplicadas: [],
        puntosTotales: 0
      };
    });
    
    console.log(`DEBUG - actualizarParticipantes - Total resultados creados: ${resultadosIniciales.length}`);
    setResultados(resultadosIniciales);
    setPilotosDNF([]);
    setExtras({
      vueltaRapida: '',
      polePosition: '',
      mejorConsistencia: '',
      segundaMejorConsistencia: ''
    });
    setHasChanges(true);
    
    alert(`Se han cargado ${resultadosIniciales.length} pilotos para esta liga.`);
  };

  // Cargar pilotos de la liga seleccionada (función manual)
  const cargarPilotosLiga = () => {
    console.log('DEBUG - cargarPilotosLiga - Cargando pilotos manualmente...');
    if (!formData.ligaId) {
      alert('No hay liga seleccionada. Por favor, selecciona una liga primero.');
      console.log('DEBUG - cargarPilotosLiga - No hay ligaId seleccionada');
      return;
    }
    
    if (!window.confirm('¿Deseas cargar todos los pilotos de la liga seleccionada?')) {
      return;
    }
    
    // Obtener la liga seleccionada para mostrar información
    const ligaSeleccionada = data.ligas.find(liga => liga.id === formData.ligaId);
    const ligaNombre = ligaSeleccionada ? ligaSeleccionada.nombre : '';
    const ligaCategoria = ligaSeleccionada ? ligaSeleccionada.categoria : '';
    
    console.log(`DEBUG - cargarPilotosLiga - Liga: ${ligaNombre}, Categoría: ${ligaCategoria}`);
    
    if (!ligaCategoria) {
      alert(`La liga ${ligaNombre} no tiene una categoría definida. Por favor, edita la liga y asigna una categoría.`);
      return;
    }
    
    // Usar la función centralizada para actualizar los resultados
    actualizarResultadosPorLiga(formData.ligaId);
    
    // Marcar que hay cambios pendientes
    setHasChanges(true);
    
    // Obtener la liga seleccionada nuevamente para mostrar el mensaje
    const ligaActualizada = data.ligas.find(liga => liga.id === formData.ligaId);
    if (ligaActualizada) {
      alert(`Se han cargado los pilotos de la liga ${ligaActualizada.nombre} con categoría ${ligaActualizada.categoria || 'sin categoría'}.`);
    } else {
      alert('Se han cargado los pilotos de la liga seleccionada.');
    }
  };
  
  // Guardar carrera
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    console.log('DEBUG - handleSubmit - Guardando carrera...');
    
    // Obtener el sistema de puntuación seleccionado
    const sistemaPuntuacionId = formData.sistemaPuntuacionId || '';
    const sistemaPuntuacion = data.sistemasPuntuacion.find(s => s.id === sistemaPuntuacionId);
    
    console.log(`DEBUG - handleSubmit - Usando sistema de puntuación: ${sistemaPuntuacion?.nombre || 'No encontrado'} (ID: ${sistemaPuntuacionId})`);
    
    // Actualizar los resultados con los extras y DNF
    const resultadosActualizados = resultados.map(resultado => {
      // Verificar si los puntos ya fueron editados manualmente
      const puntosEditadosManualmente = resultado.puntos !== undefined && resultado.puntos !== null;
      
      // Si los puntos fueron editados manualmente, respetarlos
      let puntos = puntosEditadosManualmente ? resultado.puntos : 0;
      let puntosAdicionales = 0;
      
      // Si no fueron editados manualmente, calcularlos según el sistema
      if (!puntosEditadosManualmente) {
        // Calcular puntos según la posición usando el sistema seleccionado
        if (sistemaPuntuacion && resultado.posicion <= sistemaPuntuacion.puntosPorPosicion.length) {
          // Usar el sistema de puntuación seleccionado
          puntos = sistemaPuntuacion.puntosPorPosicion[resultado.posicion - 1];
        } else if (resultado.posicion <= 24) {
          // Sistema por defecto si no se encuentra el seleccionado: 24, 23, 22, ..., 1
          puntos = 25 - resultado.posicion;
        }
        
        console.log(`DEBUG - handleSubmit - Piloto ${resultado.socioId} en posición ${resultado.posicion} recibe ${puntos} puntos base`);
        
        // Puntos adicionales por extras según el sistema seleccionado
        if (sistemaPuntuacion) {
          // Usar el sistema de puntuación seleccionado para los extras
          if (resultado.socioId === extras.vueltaRapida) {
            puntosAdicionales += sistemaPuntuacion.puntosAdicionales.vueltaRapida;
            console.log(`DEBUG - handleSubmit - Piloto ${resultado.socioId} recibe +${sistemaPuntuacion.puntosAdicionales.vueltaRapida} por vuelta rápida`);
          }
          if (resultado.socioId === extras.polePosition) {
            puntosAdicionales += sistemaPuntuacion.puntosAdicionales.polePosition;
            console.log(`DEBUG - handleSubmit - Piloto ${resultado.socioId} recibe +${sistemaPuntuacion.puntosAdicionales.polePosition} por pole position`);
          }
          if (resultado.socioId === extras.mejorConsistencia) {
            puntosAdicionales += sistemaPuntuacion.puntosAdicionales.mejorConsistencia;
            console.log(`DEBUG - handleSubmit - Piloto ${resultado.socioId} recibe +${sistemaPuntuacion.puntosAdicionales.mejorConsistencia} por mejor consistencia`);
          }
          if (resultado.socioId === extras.segundaMejorConsistencia) {
            puntosAdicionales += sistemaPuntuacion.puntosAdicionales.segundaMejorConsistencia;
            console.log(`DEBUG - handleSubmit - Piloto ${resultado.socioId} recibe +${sistemaPuntuacion.puntosAdicionales.segundaMejorConsistencia} por segunda mejor consistencia`);
          }
        } else {
          // Sistema por defecto si no se encuentra el seleccionado
          if (resultado.socioId === extras.vueltaRapida) {
            puntosAdicionales += 1; // 1 punto por vuelta rápida
            console.log(`DEBUG - handleSubmit - Piloto ${resultado.socioId} recibe +1 por vuelta rápida (sistema por defecto)`);
          }
          if (resultado.socioId === extras.polePosition) {
            puntosAdicionales += 0; // 0 puntos por pole position
            console.log(`DEBUG - handleSubmit - Piloto ${resultado.socioId} recibe +0 por pole position (sistema por defecto)`);
          }
          if (resultado.socioId === extras.mejorConsistencia) {
            puntosAdicionales += 2; // 2 puntos por mejor consistencia
            console.log(`DEBUG - handleSubmit - Piloto ${resultado.socioId} recibe +2 por mejor consistencia (sistema por defecto)`);
          }
          if (resultado.socioId === extras.segundaMejorConsistencia) {
            puntosAdicionales += 1; // 1 punto por segunda mejor consistencia
            console.log(`DEBUG - handleSubmit - Piloto ${resultado.socioId} recibe +1 por segunda mejor consistencia (sistema por defecto)`);
          }
        }
        
        // Actualizar puntos con los adicionales si no fueron editados manualmente
        puntos += puntosAdicionales;
      }
      
      // Si fueron editados manualmente, mostrar en el log
      if (puntosEditadosManualmente) {
        console.log(`DEBUG - handleSubmit - Piloto ${resultado.socioId} tiene puntos editados manualmente: ${puntos}`);
      }
      
      // Si es DNF o no se ha presentado, no recibe puntos
      const esDNF = pilotosDNF.includes(resultado.socioId);
      const esNoPresentado = pilotosNoPresentados.includes(resultado.socioId);
      
      // Buscar sanciones aplicadas a este piloto en esta carrera
      let puntosSanciones = 0;
      const sancionesPiloto = data.sanciones.filter(sancion => 
        sancion.socioId === resultado.socioId && sancion.carreraId === (id === 'new' ? '' : id)
      );
      
      // Sumar los puntos de las sanciones (pueden ser negativos)
      sancionesPiloto.forEach(sancion => {
        puntosSanciones += sancion.puntosPenalizacion;
        console.log(`DEBUG - handleSubmit - Piloto ${resultado.socioId} tiene sanción: ${sancion.nombre} con ${sancion.puntosPenalizacion} puntos`);
      });
      
      // Calcular puntos totales
      // Si es DNF, los puntos de posición son 0, pero puede recibir puntos por sanciones positivas (como asistencia)
      // Si no se ha presentado, siempre 0 puntos
      let puntosTotales = 0;
      if (esNoPresentado) {
        // No se presentó, siempre 0 puntos
        puntosTotales = 0;
      } else if (esDNF) {
        // DNF: 1 punto de consolación más cualquier sanción positiva
        puntosTotales = 1 + (puntosSanciones > 0 ? puntosSanciones : 0);
      } else {
        // Normal: puntos por posición + adicionales + sanciones
        puntosTotales = puntos + puntosAdicionales + puntosSanciones;
      }
      
      console.log(`DEBUG - handleSubmit - Piloto ${resultado.socioId} puntos totales: ${puntosTotales} (base: ${puntos}, adicionales: ${puntosAdicionales}, sanciones: ${puntosSanciones}, DNF: ${esDNF}, No Presentado: ${esNoPresentado})`);

      
      // Guardar los IDs de las sanciones aplicadas
      const sancionesIds = sancionesPiloto.map(sancion => sancion.id);
      
      return {
        ...resultado,
        puntos: puntos,
        vueltaRapida: resultado.socioId === extras.vueltaRapida,
        polePosition: resultado.socioId === extras.polePosition,
        mejorConsistencia: resultado.socioId === extras.mejorConsistencia,
        segundaMejorConsistencia: resultado.socioId === extras.segundaMejorConsistencia,
        dnf: esDNF,
        noParticipacion: esNoPresentado,
        puntosAdicionales: puntosAdicionales,
        sancionesAplicadas: sancionesIds,
        puntosTotales: puntosTotales
      };
    });
    
    console.log('DEBUG - handleSubmit - Resultados actualizados:', resultadosActualizados);
    
    try {
      if (id === 'new') {
        console.log('DEBUG - handleSubmit - Creando nueva carrera con resultados');
        // Crear nueva carrera en Firestore CON resultados
        const nuevaCarrera = {
          nombre: formData.nombre,
          ligaId: formData.ligaId,
          circuito: formData.circuito,
          fecha: formData.fecha,
          resultados: resultadosActualizados,
          pilotosDNF: pilotosDNF,
          pilotosNoPresentados: pilotosNoPresentados,
          vueltaRapida: extras.vueltaRapida,
          polePosition: extras.polePosition,
          mejorConsistencia: extras.mejorConsistencia,
          segundaMejorConsistencia: extras.segundaMejorConsistencia,
          estado: 'completada',
          notas: formData.notas || '',
          publicada: formData.publicada,
          sistemaPuntuacionId: formData.sistemaPuntuacionId
        };
        
        const carreraCreada = await CarreraServiceFirestore.create(nuevaCarrera);
        console.log('DEBUG - handleSubmit - Carrera creada:', carreraCreada);
        
        // Aplicar sanciones automáticas inmediatamente
        if (carreraCreada && carreraCreada.id) {
          const sancionesAplicadas = await sancionServiceFirestore.aplicarSancionesAutomaticas(carreraCreada.id);
          console.log(`DEBUG - handleSubmit - Se aplicaron ${sancionesAplicadas} sanciones automáticas`);
        }
      } else if (carrera) {
        console.log('DEBUG - handleSubmit - Actualizando carrera existente');
        // Actualizar carrera existente CON resultados
        await CarreraServiceFirestore.update(carrera.id, {
          nombre: formData.nombre,
          ligaId: formData.ligaId,
          circuito: formData.circuito,
          fecha: formData.fecha,
          resultados: resultadosActualizados,
          pilotosDNF: pilotosDNF,
          pilotosNoPresentados: pilotosNoPresentados,
          vueltaRapida: extras.vueltaRapida,
          polePosition: extras.polePosition,
          mejorConsistencia: extras.mejorConsistencia,
          segundaMejorConsistencia: extras.segundaMejorConsistencia,
          estado: 'completada',
          notas: formData.notas || '',
          publicada: formData.publicada
        });
        console.log('DEBUG - handleSubmit - Carrera actualizada');
        
        // Aplicar sanciones automáticas inmediatamente
        const sancionesAplicadas = await sancionServiceFirestore.aplicarSancionesAutomaticas(carrera.id);
        console.log(`DEBUG - handleSubmit - Se aplicaron ${sancionesAplicadas} sanciones automáticas`);
      }
      
      await loadData();
      console.log('DEBUG - handleSubmit - Datos recargados');
      navigate('/carreras');
    } catch (error) {
      console.error('DEBUG - handleSubmit - Error al guardar la carrera:', error);
      alert('Error al guardar la carrera. Por favor, inténtalo de nuevo.');
    }
  };

  // Manejar inicio de arrastre
  const handleDragStart = (index: number) => {
    setDraggingIndex(index);
  };
  
  // Manejar arrastre sobre otro elemento
  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    setDragOverIndex(index);
  };
  
  // Manejar soltar
  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    
    if (draggingIndex !== null && dragOverIndex !== null) {
      // Crear una copia de los resultados
      const newResultados = [...resultados];
      
      // Obtener el elemento que se está arrastrando
      const draggedItem = newResultados[draggingIndex];
      
      // Eliminar el elemento de su posición original
      newResultados.splice(draggingIndex, 1);
      
      // Insertar el elemento en la nueva posición
      newResultados.splice(dragOverIndex, 0, draggedItem);
      
      // Actualizar las posiciones
      const updatedResultados = newResultados.map((resultado, index) => ({
        ...resultado,
        posicion: index + 1
      }));
      
      // Actualizar el estado
      setResultados(updatedResultados);
      setDraggingIndex(null);
      setDragOverIndex(null);
    }
  };
  
  // Manejar fin de arrastre
  const handleDragEnd = () => {
    setDraggingIndex(null);
    setDragOverIndex(null);
  };
  
  // Función para actualizar los resultados cuando cambia la liga
  const actualizarResultadosPorLiga = (ligaId: string) => {
    console.log('DEBUG - actualizarResultadosPorLiga - Actualizando resultados para liga:', ligaId);
    
    // Obtener la liga seleccionada
    const ligaSeleccionada = data.ligas.find(liga => liga.id === ligaId);
    if (!ligaSeleccionada) {
      console.log('DEBUG - actualizarResultadosPorLiga - Liga no encontrada');
      return;
    }
    
    console.log('DEBUG - actualizarResultadosPorLiga - Liga seleccionada:', ligaSeleccionada);
    
    // Obtener la categoría de la liga
    const categoriaFiltro = ligaSeleccionada.categoria || '';
    if (!categoriaFiltro) {
      console.log('DEBUG - actualizarResultadosPorLiga - La liga no tiene categoría definida');
      return;
    }
    
    // Filtrar socios por Categoria
    const sociosFiltrados = data.socios.filter(socio => {
      const socioCategoria = (socio.Categoria || '').trim().toUpperCase();
      const categoriaFiltroUpper = categoriaFiltro.trim().toUpperCase();
      return socioCategoria === categoriaFiltroUpper;
    });
    
    // Crear nuevos resultados con los pilotos filtrados
    const resultadosIniciales: ResultadoCarrera[] = sociosFiltrados.map((socio, idx) => ({
      id: `resultado_${socio.id}_${Date.now()}_${idx}`,
      socioId: socio.id,
      carreraId: '',
      posicion: idx + 1,
      puntos: 0,
      vueltaRapida: false,
      pole: false,
      polePosition: false,
      mejorConsistencia: false,
      segundaMejorConsistencia: false,
      dnf: false,
      noParticipacion: false,
      incidentes: 0,
      salidasPista: 0,
      puntosAdicionales: 0,
      sancionesAplicadas: [],
      puntosTotales: 0
    }));
    
    setResultados(resultadosIniciales);
    setPilotosDNF([]);
    setExtras({
      vueltaRapida: '',
      polePosition: '',
      mejorConsistencia: '',
      segundaMejorConsistencia: ''
    });
    
    console.log(`DEBUG - actualizarResultadosPorLiga - Resultados actualizados con ${resultadosIniciales.length} pilotos`);
  }
  
  // Manejar cambio en los extras
  const handleExtraChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const { name, value } = e.target;
    console.log(`DEBUG - handleExtraChange - Cambiando ${name} a ${value}`);
    setExtras(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  // La función handleDNFChange ya está definida arriba
  
  // Mover piloto hacia arriba
  const handleMoveUp = (index: number) => {
    if (index === 0) return;
    
    const newResultados = [...resultados];
    const temp = newResultados[index];
    newResultados[index] = newResultados[index - 1];
    newResultados[index - 1] = temp;
    
    // Actualizar las posiciones
    const updatedResultados = newResultados.map((resultado, idx) => ({
      ...resultado,
      posicion: idx + 1
    }));
    
    setResultados(updatedResultados);
  };
  
  // Mover piloto hacia abajo
  const handleMoveDown = (index: number) => {
    if (index === resultados.length - 1) return;
    
    const newResultados = [...resultados];
    const temp = newResultados[index];
    newResultados[index] = newResultados[index + 1];
    newResultados[index + 1] = temp;
    
    // Actualizar las posiciones
    const updatedResultados = newResultados.map((resultado, idx) => ({
      ...resultado,
      posicion: idx + 1
    }));
    
    setResultados(updatedResultados);
  };
  

  
  // Obtener nombre del socio por ID
  const getNombreSocio = (socioId: string) => {
    // Para los pilotos que ya están en los resultados, no necesitamos filtrar por categoría
    // ya que estos pilotos ya fueron seleccionados correctamente
    const socio = data.socios.find(s => s.id === socioId);
    
    if (!socio) {
      console.log(`DEBUG - getNombreSocio - Socio no encontrado con ID: ${socioId}`);
      return 'Piloto no encontrado';
    }
    
    // Usar display_name si está disponible, de lo contrario usar nombre y apellido
    if (socio.display_name) {
      return socio.display_name;
    } else if (socio.nombre) {
      return socio.apellido ? `${socio.nombre} ${socio.apellido}` : socio.nombre;
    } else {
      return 'Nombre no disponible';
    }
  };
  

  
  return (
    <Container>
      <Header>
        <Title><FaFlag /> {id === 'new' ? 'Nueva Carrera' : 'Editar Carrera'}</Title>
        <ButtonGroup>
          <BackButton onClick={() => navigate('/carreras')}>
            <FaArrowLeft /> Volver
          </BackButton>
          <Button 
            onClick={() => setShowImportDialog(true)}
            style={{ backgroundColor: theme.colors.info.main, marginRight: '10px' }}
          >
            <FaFileImport /> Importar Resultados
          </Button>
          <Button onClick={handleSubmit}>
            <FaSave /> Guardar
          </Button>
        </ButtonGroup>
      </Header>
      
      <Form onSubmit={handleSubmit}>
        <SectionTitle>
          <FaTrophy /> Información de la Carrera
        </SectionTitle>
        
        <FormGroup>
          <Label htmlFor="nombre">Nombre</Label>
          <Input
            type="text"
            id="nombre"
            name="nombre"
            value={formData.nombre}
            onChange={handleChange}
            required
          />
        </FormGroup>

        <FormGroup>
          <Label htmlFor="ligaId"><FaTrophy /> Liga</Label>
          <Select
            id="ligaId"
            name="ligaId"
            value={formData.ligaId}
            onChange={handleLigaChange}
            required
          >
            <option value="">Selecciona una liga</option>
            {data.ligas.map(liga => (
              <option key={liga.id} value={liga.id}>{liga.nombre}</option>
            ))}
          </Select>
        </FormGroup>

        <FormGroup>
          <Label htmlFor="sistemaPuntuacionId"><FaCalculator /> Sistema de Puntuación</Label>
          <div style={{ display: 'flex', gap: '10px' }}>
            <div style={{ flex: 1 }}>
              <Select
                id="sistemaPuntuacionId"
                name="sistemaPuntuacionId"
                value={formData.sistemaPuntuacionId}
                onChange={handleSistemaPuntuacionChange}
              >
                <option value="">Selecciona un sistema de puntuación</option>
                {data.sistemasPuntuacion.map(sistema => (
                  <option key={sistema.id} value={sistema.id}>{sistema.nombre}</option>
                ))}
              </Select>
            </div>
            <button 
              type="button" 
              onClick={() => {
                if (resultados.length > 0) {
                  const updatedResultados = resultados.map(resultado => ({
                    ...resultado,
                    puntos: calcularPuntosPiloto(resultado.posicion, resultado.socioId)
                  }));
                  setResultados(updatedResultados);
                  setHasChanges(true);
                  alert('Puntos recalculados según el sistema seleccionado');
                }
              }}
              style={{
                padding: '8px 16px',
                backgroundColor: '#0f3460',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '0.9rem'
              }}
            >
              <FaCalculator style={{ marginRight: '6px' }} /> Recalcular Puntos
            </button>
          </div>
          <div style={{ fontSize: '0.85em', color: '#6c757d', marginTop: '5px' }}>
            Por defecto se usa el sistema de la liga, pero puedes seleccionar uno específico para esta carrera.
            Los puntos se pueden editar manualmente en la tabla de resultados.
          </div>
        </FormGroup>

        <FormGroup>
          <Label htmlFor="circuito">Circuito</Label>
          <select
            id="circuito"
            name="circuito"
            value={formData.circuito}
            onChange={handleChange}
            required
            style={{ width: '100%', padding: '12px 16px', border: '1px solid rgba(0,0,0,0.1)', borderRadius: '4px', fontSize: '1rem', backgroundColor: theme.colors.background.default, color: theme.colors.text.primary }}
          >
            <option value="">Selecciona un circuito...</option>
            {CIRCUITOS.map(circuito => (
              <option key={circuito} value={circuito}>{circuito}</option>
            ))}
          </select>
        </FormGroup>
        
        <FormGroup>
          <Label htmlFor="fecha"><FaFlag /> Fecha</Label>
          <Input
            type="date"
            id="fecha"
            name="fecha"
            value={formData.fecha}
            onChange={handleChange}
            required
          />
        </FormGroup>
        
        <FormGroup style={{ marginBottom: '20px' }}>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <input
              type="checkbox"
              id="publicada"
              name="publicada"
              checked={formData.publicada}
              onChange={(e) => setFormData(prev => ({ ...prev, publicada: e.target.checked }))}
              style={{ marginRight: '10px' }}
            />
            <Label htmlFor="publicada" style={{ margin: 0 }}>
              Publicar en clasificación general
            </Label>
          </div>
          <div style={{ fontSize: '0.85em', color: '#6c757d', marginTop: '5px' }}>
            Si está marcado, esta carrera aparecerá en la clasificación general de la liga. Desmarca esta opción para carreras futuras o que aún no deseas incluir en la clasificación.
          </div>
        </FormGroup>
        
        <Button type="submit">
          <FaSave /> Guardar
        </Button>
      </Form>
      
      {formData.ligaId && (
        <Card>
          <CardTitle>
            <FaMedal /> Resultados de la Carrera
          </CardTitle>
          
          {resultados.length === 0 ? (
            <>
              <InfoMessage>
                Para gestionar los resultados de la carrera, primero debes cargar todos los pilotos de la liga seleccionada.
                Una vez cargados, podrás ordenarlos según su posición final arrastrándolos o usando los botones de flechas.
              </InfoMessage>
              
              <Button onClick={cargarPilotosLiga}>
                <FaPlus /> Cargar Pilotos de la Liga
              </Button>
              
              <InfoMessage style={{ marginTop: '10px', backgroundColor: '#f8d7da', color: '#721c24' }}>
                Nota: Normalmente los pilotos se cargan automáticamente. Si no se han cargado, usa este botón para cargarlos manualmente.
              </InfoMessage>
            </>
          ) : (
            <>
              <InfoMessage>
                Arrastra los pilotos o usa los botones de flechas para ordenarlos según su posición final en la carrera.
                Los puntos se asignarán automáticamente según el orden y los extras seleccionados.
              </InfoMessage>
              
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '20px', marginBottom: '20px' }}>
                {/* Pole Position Card */}
                <div style={{ flex: '1', minWidth: '220px', maxWidth: '300px', backgroundColor: '#fff', borderRadius: '8px', padding: '15px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
                  <h4 style={{ color: '#0f3460', marginTop: 0, marginBottom: '15px', borderBottom: '1px solid #eee', paddingBottom: '8px' }}>
                    <span style={{ backgroundColor: '#f8f9fa', borderRadius: '4px', padding: '3px 6px', fontSize: '0.8em', marginLeft: '5px', color: '#6c757d', float: 'right' }}>+0 pts</span>
                    Pole Position
                  </h4>
                  <Select
                    id="polePosition"
                    name="polePosition"
                    value={extras.polePosition}
                    onChange={handleExtraChange}
                    style={{ width: '100%', backgroundColor: extras.polePosition ? '#f8f9fa' : 'white' }}
                  >
                    <option value="">Seleccionar Piloto</option>
                    {resultados.map(resultado => (
                      <option key={resultado.socioId} value={resultado.socioId}>
                        {getNombreSocio(resultado.socioId)}
                      </option>
                    ))}
                  </Select>
                </div>
                
                {/* Vuelta Rápida Card */}
                <div style={{ flex: '1', minWidth: '220px', maxWidth: '300px', backgroundColor: '#fff', borderRadius: '8px', padding: '15px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
                  <h4 style={{ color: '#0f3460', marginTop: 0, marginBottom: '15px', borderBottom: '1px solid #eee', paddingBottom: '8px' }}>
                    <span style={{ backgroundColor: '#e2f0ff', borderRadius: '4px', padding: '3px 6px', fontSize: '0.8em', marginLeft: '5px', color: '#0f3460', float: 'right' }}>+1 pts</span>
                    Vuelta Rápida
                  </h4>
                  <Select
                    id="vueltaRapida"
                    name="vueltaRapida"
                    value={extras.vueltaRapida}
                    onChange={handleExtraChange}
                    style={{ width: '100%', backgroundColor: extras.vueltaRapida ? '#e2f0ff' : 'white' }}
                  >
                    <option value="">Seleccionar Piloto</option>
                    {resultados.map(resultado => (
                      <option key={resultado.socioId} value={resultado.socioId}>
                        {getNombreSocio(resultado.socioId)}
                      </option>
                    ))}
                  </Select>
                </div>
                
                {/* Mejor Consistencia Card */}
                <div style={{ flex: '1', minWidth: '220px', maxWidth: '300px', backgroundColor: '#fff', borderRadius: '8px', padding: '15px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
                  <h4 style={{ color: '#0f3460', marginTop: 0, marginBottom: '15px', borderBottom: '1px solid #eee', paddingBottom: '8px' }}>
                    <span style={{ backgroundColor: '#d1e7dd', borderRadius: '4px', padding: '3px 6px', fontSize: '0.8em', marginLeft: '5px', color: '#0f5132', float: 'right' }}>+2 pts</span>
                    Mejor Consist.
                  </h4>
                  <Select
                    id="mejorConsistencia"
                    name="mejorConsistencia"
                    value={extras.mejorConsistencia}
                    onChange={handleExtraChange}
                    style={{ width: '100%', backgroundColor: extras.mejorConsistencia ? '#d1e7dd' : 'white' }}
                  >
                    <option value="">Seleccionar Piloto</option>
                    {resultados.map(resultado => (
                      <option key={resultado.socioId} value={resultado.socioId}>
                        {getNombreSocio(resultado.socioId)}
                      </option>
                    ))}
                  </Select>
                </div>
                
                {/* Segunda Mejor Consistencia Card */}
                <div style={{ flex: '1', minWidth: '220px', maxWidth: '300px', backgroundColor: '#fff', borderRadius: '8px', padding: '15px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
                  <h4 style={{ color: '#0f3460', marginTop: 0, marginBottom: '15px', borderBottom: '1px solid #eee', paddingBottom: '8px' }}>
                    <span style={{ backgroundColor: '#e2f0ff', borderRadius: '4px', padding: '3px 6px', fontSize: '0.8em', marginLeft: '5px', color: '#0f3460', float: 'right' }}>+1 pts</span>
                    Segunda Mejor Consist.
                  </h4>
                  <Select
                    id="segundaMejorConsistencia"
                    name="segundaMejorConsistencia"
                    value={extras.segundaMejorConsistencia}
                    onChange={handleExtraChange}
                    style={{ width: '100%', backgroundColor: extras.segundaMejorConsistencia ? '#e2f0ff' : 'white' }}
                  >
                    <option value="">Seleccionar Piloto</option>
                    {resultados.map(resultado => (
                      <option key={resultado.socioId} value={resultado.socioId}>
                        {getNombreSocio(resultado.socioId)}
                      </option>
                    ))}
                  </Select>
                </div>
              </div>
              
              <Table style={{ marginTop: '20px' }}>
  <thead>
    <tr>
      <th style={{ width: '60px' }}>Pos.</th>
      <th>Piloto</th>
      <th>Extras</th>
      <th style={{ width: '80px' }}>Puntos</th>
      <th>DNF</th>
      <th>No Presentado</th>
      <th style={{ width: '120px' }}>Acciones</th>
    </tr>
  </thead>
  <tbody>
    {resultados.map((resultado, index) => (
      <ResultadoRow
        key={resultado.socioId}
        draggable
        onDragStart={() => handleDragStart(index)}
        onDragOver={(e) => handleDragOver(e, index)}
        onDrop={handleDrop}
        onDragEnd={handleDragEnd}
        $isDragging={index === draggingIndex}
      >
        <Td>{resultado.posicion}</Td>
        <Td>
          {getNombreSocio(resultado.socioId)}
          {extras.polePosition === resultado.socioId && (
            <FaFlag style={{ marginLeft: '5px', color: '#e94560' }} title="Pole Position" />
          )}
          {extras.vueltaRapida === resultado.socioId && (
            <FaStopwatch style={{ marginLeft: '5px', color: '#0f3460' }} title="Vuelta Rápida" />
          )}
          {extras.mejorConsistencia === resultado.socioId && (
            <FaHourglass style={{ marginLeft: '5px', color: '#0f3460' }} title="Mejor Consistencia" />
          )}
          {extras.segundaMejorConsistencia === resultado.socioId && (
            <FaHourglass style={{ marginLeft: '5px', color: '#6c757d' }} title="Segunda Mejor Consistencia" />
          )}
        </Td>
        <Td>
          {extras.polePosition === resultado.socioId && 'Pole (+2) '}
          {extras.vueltaRapida === resultado.socioId && 'VR (+1) '}
          {extras.mejorConsistencia === resultado.socioId && 'MC (+2) '}
          {extras.segundaMejorConsistencia === resultado.socioId && 'SMC (+1)'}
        </Td>
        <Td>
          <input 
            type="number" 
            value={resultado.puntos || 0}
            onChange={(e) => {
              const newPuntos = parseInt(e.target.value) || 0;
              const updatedResultados = [...resultados];
              updatedResultados[index] = {
                ...resultado,
                puntos: newPuntos
              };
              setResultados(updatedResultados);
              setHasChanges(true);
            }}
            style={{
              width: '60px',
              padding: '4px 8px',
              border: '1px solid #ddd',
              borderRadius: '4px',
              textAlign: 'center'
            }}
          />
        </Td>
        <Td>
          <input
            type="checkbox"
            checked={pilotosDNF.includes(resultado.socioId)}
            onChange={() => handleDNFChange(resultado.socioId)}
          />
        </Td>
        <Td>
          <input
            type="checkbox"
            checked={pilotosNoPresentados.includes(resultado.socioId)}
            onChange={() => handleNoPresentadoChange(resultado.socioId)}
          />
        </Td>
        <Td>
          <ActionButton 
            onClick={() => handleMoveUp(index)} 
            disabled={index === 0}
            style={{ opacity: index === 0 ? 0.5 : 1 }}
          >
            <FaArrowUp />
          </ActionButton>
          <ActionButton 
            onClick={() => handleMoveDown(index)} 
            disabled={index === resultados.length - 1}
            style={{ opacity: index === resultados.length - 1 ? 0.5 : 1 }}
          >
            <FaArrowDown />
          </ActionButton>
          <DragHandle title="Arrastrar para reordenar">
            ⋮⋮
          </DragHandle>
        </Td>
      </ResultadoRow>
    ))}
  </tbody>
</Table>
            </>
          )}
        </Card>
      )}
      
      {/* Modal de importación de resultados */}
      {showImportDialog && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            padding: '20px',
            borderRadius: '8px',
            maxWidth: '800px',
            maxHeight: '90vh',
            overflowY: 'auto',
            width: '90%'
          }}>
            <h2>Importar Resultados de Carrera</h2>
            <p>Pega aquí los resultados de la carrera. Formato esperado:</p>
            <pre style={{
              backgroundColor: '#f5f5f5',
              padding: '10px',
              borderRadius: '4px',
              marginBottom: '15px',
              whiteSpace: 'pre-wrap',
              maxHeight: '200px',
              overflowY: 'auto'
            }}>
{`1 Jose Martinez [Los Solfamidas] 60:50.550 01:31.397 39 (36) 2 7 (0)
2 Oscar Castro [Luca GT] 61:06.161 +15.611 01:31.612 39 (35) 3 4 (2)
...
DNF Fran Herrero [Los Solfamidas] --.--.--- 01:32.247 33 (25) 8 31 (8)`}
            </pre>
            <ImportResultadosCarrera 
              socios={data.socios} 
              onImport={handleImportResultados}
            />
            <div style={{ marginTop: '20px', textAlign: 'right' }}>
              <Button 
                onClick={() => setShowImportDialog(false)}
                style={{ backgroundColor: '#6c757d' }}
              >
                Cerrar
              </Button>
            </div>
          </div>
        </div>
      )}
    </Container>
  );
}
export default CarreraDetail;
